import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { MdContentCopy, MdRefresh } from 'react-icons/md';
import { Button, ConfirmDialog, CONFIRM_DIALOG_TONE_CAUTION, Spinner } from '@aibuild/ui';
import type { PublicLinkState } from '@api/appBuilder';
import useSnackbar from '@hooks/useSnackbar';
import { sharedAppPath } from '../../appRunPath';
import { Actions, LinkBox, LinkRow, ProblemList } from './ShareAppDialog.styled';

interface PublicLinkSectionProps {
  readonly state: PublicLinkState | undefined;
  readonly loading: boolean;
  readonly busy: boolean;
  readonly onNewLink: () => Promise<unknown>;
  readonly onFailed: (err: unknown) => void;
}

const sentence = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

const PublicLinkSection: React.FC<PublicLinkSectionProps> = ({ state, loading, busy, onNewLink, onFailed }) => {
  const intl = useIntl();
  const { showSuccess } = useSnackbar();
  const [confirmingNew, setConfirmingNew] = useState(false);
  const token = state?.token ?? null;
  const problems = state?.problems ?? [];
  const url = token ? `${globalThis.location.origin}${sharedAppPath(token)}` : null;

  if (loading) return <Spinner label="" />;

  const copy = () => {
    if (!url) return;
    navigator.clipboard
      .writeText(url)
      .then(() => showSuccess(intl.formatMessage({ id: 'apps.publicLink.copied', defaultMessage: 'Link copied' })))
      .catch(onFailed);
  };

  return (
    <>
      {problems.length > 0 && (
        <>
          <span>
            {intl.formatMessage({ id: 'apps.publicLink.problems', defaultMessage: 'This app cannot be shared publicly yet:' })}
          </span>
          <ProblemList data-testid="public-link__problems">
            {problems.map((problem) => (
              <li key={problem}>{sentence(problem)}</li>
            ))}
          </ProblemList>
        </>
      )}
      {url && problems.length === 0 && (
        <>
          <LinkRow>
            <LinkBox data-dd-privacy="mask" data-testid="public-link__url">
              {url}
            </LinkBox>
            <Button size="sm" leadingIcon={<MdContentCopy size={16} />} onClick={copy} dataTestId="public-link__copy">
              {intl.formatMessage({ id: 'apps.publicLink.copy', defaultMessage: 'Copy link' })}
            </Button>
          </LinkRow>
          <Actions>
            <Button
              variant="outlined"
              size="sm"
              leadingIcon={<MdRefresh size={16} />}
              disabled={busy}
              onClick={() => setConfirmingNew(true)}
              dataTestId="public-link__new"
            >
              {intl.formatMessage({ id: 'apps.publicLink.new', defaultMessage: 'Create a new link' })}
            </Button>
          </Actions>
        </>
      )}
      <ConfirmDialog
        open={confirmingNew}
        onClose={() => setConfirmingNew(false)}
        onConfirm={() => onNewLink().then(() => undefined, onFailed)}
        tone={CONFIRM_DIALOG_TONE_CAUTION}
        title={intl.formatMessage({ id: 'apps.publicLink.new.title', defaultMessage: 'Create a new link?' })}
        description={intl.formatMessage({
          id: 'apps.publicLink.new.body',
          defaultMessage:
            'The current link stops working for everyone who has it, including people who signed up through it: they can still see their runs but can no longer run the app.',
        })}
        confirmLabel={intl.formatMessage({ id: 'apps.publicLink.new.confirm', defaultMessage: 'Create new link' })}
        dataTestId="public-link__new-dialog"
      />
    </>
  );
};

export default PublicLinkSection;
